import { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router";
import { useAuthStore } from "@/modules/auth/stores/auth-store";
import Input from "@/components/ui/input";
import ImageUploadField from "@/components/ui/image-upload-field";
import Button from "@/components/ui/button";
import Container from "@/components/ui/container";
import IconArrowLeftLane from "@/components/icons/icon-arrow-left-lane";

// Helper to convert data URL to File
function dataURLtoFile(dataUrl: string, baseFilename: string): File {
	const arr = dataUrl.split(",");
	const mime = arr[0].match(/:(.*?);/)?.[1] || "image/jpeg";
	const bstr = atob(arr[1]);
	let n = bstr.length;
	const u8arr = new Uint8Array(n);
	while (n--) {
		u8arr[n] = bstr.charCodeAt(n);
	}
	// Get correct extension based on MIME type
	const extMap: Record<string, string> = {
		"image/jpeg": ".jpg",
		"image/png": ".png",
		"image/gif": ".gif",
		"image/webp": ".webp",
	};
	const ext = extMap[mime] || ".jpg";
	const filename = baseFilename.replace(/\.[^/.]+$/, "") + ext;
	return new File([u8arr], filename, { type: mime });
}

type SaveStatus = "idle" | "saving" | "saved";

export default function ProfilePage() {
	const { user, currentWorkspaceId, updateProfile, updateAvatar, deleteAvatar } = useAuthStore();

	// Form states
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [avatar, setAvatar] = useState<string | null>(null);
	const [pendingAvatarDataUrl, setPendingAvatarDataUrl] = useState<string | null>(null); // New avatar to upload
	const [avatarMarkedForDeletion, setAvatarMarkedForDeletion] = useState(false); // Track if avatar should be deleted

	// Save status state
	const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
	const savedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Track original values for change detection
	const originalFirstNameRef = useRef("");
	const originalLastNameRef = useRef("");
	const originalAvatarRef = useRef<string | null>(null);

	// Sync local state with user when it loads
	useEffect(() => {
		if (user) {
			setFirstName(user.first_name || "");
			setLastName(user.last_name || "");
			setAvatar(user.avatar_url);
			originalFirstNameRef.current = user.first_name || "";
			originalLastNameRef.current = user.last_name || "";
			originalAvatarRef.current = user.avatar_url;
			setPendingAvatarDataUrl(null);
			setAvatarMarkedForDeletion(false);
		}
	}, [user]);

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (savedTimeoutRef.current) {
				clearTimeout(savedTimeoutRef.current);
			}
		};
	}, []);

	// Check if there are unsaved changes
	const hasTextChanges = firstName !== originalFirstNameRef.current || lastName !== originalLastNameRef.current;
	const hasAvatarChanges = pendingAvatarDataUrl !== null || avatarMarkedForDeletion;
	const hasChanges = hasTextChanges || hasAvatarChanges;

	// Manual save function
	const handleSave = useCallback(async () => {
		if (!hasChanges) return;

		// Clear any existing timeout
		if (savedTimeoutRef.current) {
			clearTimeout(savedTimeoutRef.current);
		}

		setSaveStatus("saving");

		try {
			// Save text fields if changed
			if (hasTextChanges) {
				await updateProfile({ first_name: firstName, last_name: lastName });
				originalFirstNameRef.current = firstName;
				originalLastNameRef.current = lastName;
			}

			// Handle avatar changes
			if (avatarMarkedForDeletion) {
				await deleteAvatar();
				originalAvatarRef.current = null;
				setAvatarMarkedForDeletion(false);
			} else if (pendingAvatarDataUrl) {
				const file = dataURLtoFile(pendingAvatarDataUrl, "avatar.jpg");
				const result = await updateAvatar(file);
				if (result) {
					setAvatar(result.avatar_url);
					originalAvatarRef.current = result.avatar_url;
				}
				setPendingAvatarDataUrl(null);
			}

			setSaveStatus("saved");

			// Hide "Saved" after 2 seconds
			savedTimeoutRef.current = setTimeout(() => {
				setSaveStatus("idle");
			}, 2000);
		} catch (error) {
			console.error("Failed to save profile:", error);
			setSaveStatus("idle");
		}
	}, [firstName, lastName, hasChanges, hasTextChanges, pendingAvatarDataUrl, avatarMarkedForDeletion, updateProfile, updateAvatar, deleteAvatar]);

	// Discard changes
	const handleDiscard = useCallback(() => {
		setFirstName(originalFirstNameRef.current);
		setLastName(originalLastNameRef.current);
		setAvatar(originalAvatarRef.current);
		setPendingAvatarDataUrl(null);
		setAvatarMarkedForDeletion(false);
		setSaveStatus("idle");
	}, []);

	// Avatar change handler - only updates local state, doesn't upload
	const handleAvatarChange = useCallback((dataUrl: string | null) => {
		if (dataUrl) {
			// New avatar selected - show it locally and mark for upload
			setAvatar(dataUrl);
			setPendingAvatarDataUrl(dataUrl);
			setAvatarMarkedForDeletion(false);
		} else {
			// Avatar cleared - mark for deletion
			setAvatar(null);
			setPendingAvatarDataUrl(null);
			setAvatarMarkedForDeletion(originalAvatarRef.current !== null);
		}
	}, []);

	return (
		<div className="relative py-12">
			<Container className="pb-20">
				{/* Back Button */}
				{currentWorkspaceId && (
					<div className="mb-6">
						<Button as={Link} to={`/${currentWorkspaceId}/dashboard`} variant="soft" className="inline-flex items-center gap-2">
							<IconArrowLeftLane />
							Back to Selected Brand
						</Button>
					</div>
				)}

				{/* Page Header */}
				<div className="mb-8">
					<h1 className="text-xl font-semibold text-primary">Profile</h1>
					<p className="mt-1 text-sm text-tertiary">Manage your personal information</p>
				</div>

				<div className="space-y-10">
					{/* Personal Information Section */}
					<section className="bg-white rounded-xl border border-gray-200 p-6">
						<div className="mb-6">
							<h2 className="text-sm font-semibold text-primary uppercase tracking-wide">Personal Information</h2>
							<p className="text-sm text-tertiary mt-1">Update your personal details.</p>
						</div>

						<div className="space-y-6">
							<Input
								id="email"
								label="Email"
								type="email"
								value={user?.email || ""}
								disabled
								message={{ text: "Email cannot be changed", status: "message" }}
							/>

							<Input
								id="first-name"
								label="First Name"
								type="text"
								placeholder="Enter your first name"
								value={firstName}
								onChange={(e) => setFirstName(e.target.value)}
								disabled={saveStatus === "saving"}
							/>

							<Input
								id="last-name"
								label="Last Name"
								type="text"
								placeholder="Enter your last name"
								value={lastName}
								onChange={(e) => setLastName(e.target.value)}
								disabled={saveStatus === "saving"}
							/>
						</div>
					</section>

					{/* Profile Picture Section */}
					<section className="bg-white rounded-xl border border-gray-200 p-6">
						<div className="mb-6">
							<h2 className="text-sm font-semibold text-primary uppercase tracking-wide">Profile Picture</h2>
							<p className="text-sm text-tertiary mt-1">Upload a profile picture to personalize your account.</p>
						</div>

						<div className="flex items-start gap-6">
							<div className="flex-shrink-0">
								<ImageUploadField
									value={avatar}
									onChange={handleAvatarChange}
									aspectRatio={1}
									maxSizeMB={2}
									maxDimension={256}
									className="w-24 h-24"
									disabled={saveStatus === "saving"}
								/>
							</div>
							<div className="flex-1 pt-1">
								<p className="text-sm text-gray-500">Maximum size: 256x256 pixels.</p>
								<p className="mt-1 text-xs text-gray-400">Supports JPG, PNG, GIF, WebP. Max 2MB.</p>
							</div>
						</div>
					</section>
				</div>
			</Container>

			{/* Sticky footer with Save/Discard buttons */}
			<div className="fixed bottom-0 left-0 z-20 bg-white border-t border-gray-200 px-8 py-4 w-full">
				<Container>
					<div className="flex items-center justify-end">
						<div className="flex items-center gap-3">
							{/* Status indicator */}
							{saveStatus !== "idle" && (
								<span
									className={`text-sm font-medium flex items-center gap-1.5 transition-opacity duration-200 ${
										saveStatus === "saving" ? "text-gray-500" : "text-green-600"
									}`}
								>
									{saveStatus === "saving" ? (
										<>
											<svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
												<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
												<path
													className="opacity-75"
													fill="currentColor"
													d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
												/>
											</svg>
											Saving...
										</>
									) : (
										<>
											<svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
												<path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
											</svg>
											Saved
										</>
									)}
								</span>
							)}

							{/* Discard button */}
							<Button
								type="button"
								variant="light"
								onClick={handleDiscard}
								disabled={!hasChanges || saveStatus === "saving" || saveStatus === "saved"}
							>
								Discard
							</Button>

							{/* Save button */}
							<Button
								type="button"
								variant="default"
								onClick={handleSave}
								disabled={!hasChanges || saveStatus === "saving" || saveStatus === "saved"}
							>
								{saveStatus === "saving" ? "Saving..." : "Save changes"}
							</Button>
						</div>
					</div>
				</Container>
			</div>
		</div>
	);
}
